import type { TaskListItem } from "@relais/shared";
import { supabase } from "./supabase.js";

export class ApiError extends Error {
  constructor(public status: number, public code: string, message: string) {
    super(message);
  }
}

// Si VITE_API_URL est fourni (déploiement cross-domain), on l'utilise comme
// préfixe absolu. Sinon, chemin relatif → proxy Vite en dev, même origine en
// prod. Strip d'un trailing slash pour éviter `//api/v1`.
const API_BASE = (
  (import.meta.env.VITE_API_URL as string | undefined) ?? ""
).replace(/\/$/, "");

async function getToken(): Promise<string | null> {
  const { data } = await supabase.auth.getSession();
  return data.session?.access_token ?? null;
}

export async function apiFetch<T>(
  path: string,
  init: RequestInit = {},
): Promise<T> {
  const token = await getToken();
  const headers = new Headers(init.headers);
  headers.set("content-type", "application/json");
  if (token) headers.set("authorization", `Bearer ${token}`);

  const r = await fetch(`${API_BASE}/api/v1${path}`, { ...init, headers });
  const text = await r.text();
  const body = text ? JSON.parse(text) : null;
  if (!r.ok) {
    throw new ApiError(
      r.status,
      body?.code ?? "http_error",
      body?.message ?? `http ${r.status}`,
    );
  }
  return body as T;
}

/**
 * Variante binaire d'`apiFetch` : même base d'URL, même jeton, mais la réponse
 * est rendue telle quelle. Sert au compte rendu PDF produit par le serveur, le
 * seul document qui fasse foi une fois la mission validée.
 *
 * Les erreurs restent en JSON côté serveur : on les relit comme `apiFetch`
 * pour que l'appelant reçoive le même `ApiError` qu'ailleurs.
 */
export async function apiFetchBlob(
  path: string,
  init: RequestInit = {},
): Promise<Blob> {
  const token = await getToken();
  const headers = new Headers(init.headers);
  if (token) headers.set("authorization", `Bearer ${token}`);

  const r = await fetch(`${API_BASE}/api/v1${path}`, { ...init, headers });
  if (!r.ok) {
    const text = await r.text().catch(() => "");
    let code = "http_error";
    let message = `http ${r.status}`;
    try {
      const body = text ? JSON.parse(text) : null;
      code = body?.code ?? code;
      message = body?.message ?? message;
    } catch {
      // Corps non-JSON (proxy, page d'erreur) : on garde le message par défaut.
    }
    throw new ApiError(r.status, code, message);
  }
  return r.blob();
}

// Point d'entrée UNIQUE pour lister les tâches depuis relais-front. Ce front ne
// montre que les tâches assignées à l'acteur (scope=mine) : sans ça, un
// admin-assignee (qui a le droit de tout voir) verrait toutes les tâches
// système, noyant les siennes. Ne JAMAIS appeler GET /tasks sans scope ici —
// passer par ce helper garantit qu'aucun écran (liste, activité…) n'oublie.
export function fetchMyTasks(since?: string): Promise<TaskListItem[]> {
  const qs = since ? `&since=${encodeURIComponent(since)}` : "";
  return apiFetch<TaskListItem[]>(`/tasks?scope=mine${qs}`);
}
